import {
  Report_ElementType as ElementType,
  Maybe,
  Report_ElementQueryOptionsInput,
  Report_ElementStatus,
  Report_RoundingValue,
  Report_ValueType,
} from "@/services/graphql/reactQuery";
import { PaxChartElement } from "@/shared/components/PaxChartElement";
import {
  AxisFormat,
  AxisRounding,
  ChartElementProps,
  ColumnType,
  ElementConfig,
  PaxTableCell,
  PaxTableColumnHeader,
  PaxTableRow,
  ReportElementData,
  RowClass,
  SortDirection,
} from "@/shared/types/element";
import { formatDisplayValue } from "@/shared/utility/stringFormatters";
import {
  Box,
  CardContent,
  Checkbox,
  Skeleton,
  SxProps,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Theme,
  Typography,
  TypographyProps,
} from "@mui/material";
import {
  EmptyBoxIllustration,
  nCinoColors,
  NetworkErrorIllustration,
  PaxBoxElement,
  PaxBoxElementProps,
  PaxCallToAction,
  PaxElementWrapper,
  PaxElementWrapperProps,
  PaxSkeletonLoader,
  PaxTableProps,
  PaxTooltip,
  SortableHeaderCell,
  TooltipCell,
} from "@ncino/pax-ui";
import { UseNavigateResult } from "@tanstack/react-router";
import type Highcharts from "highcharts";
import * as HighchartsLib from "highcharts";
import PaxListElement, { PaxListElementProps } from "./PaxListElement";
import PaxMatrixElement, { resolveMatrixData } from "./PaxMatrixElement";
import PaxTableElement, { PaxTableElementProps } from "./PaxTableElement";

// Revive serialized function strings back into real functions with Highcharts in scope.
const formatChartJson = (inObject: unknown): unknown => {
  if (typeof inObject === "string" && /^\s*function[\s(]/.test(inObject)) {
    try {
      return new Function("Highcharts", `return (${inObject.trim()})`)(HighchartsLib);
    } catch {
      return inObject;
    }
  }
  if (typeof inObject !== "object" || inObject === null) return inObject;
  if (Array.isArray(inObject)) return inObject.map(formatChartJson);
  const out: Record<string, unknown> = {};
  for (const key in inObject as Record<string, unknown>) {
    out[key] = formatChartJson((inObject as Record<string, unknown>)[key]);
  }
  return out;
};

function resolveChartData(chartData: string[] | null | undefined): Highcharts.Options {
  if (!chartData?.length) return {};
  const allOptions = chartData.map((raw) => {
    const parsed = JSON.parse(raw);
    if (parsed.chart) delete parsed.chart.width;
    return formatChartJson(parsed) as Highcharts.Options;
  });

  const base = allOptions[0];
  if ((base.series as unknown[]).length > 1) {
    base.legend = { ...((base.legend as Highcharts.LegendOptions) ?? {}), enabled: true };
  }

  return base;
}

export interface SortConfig {
  sortColumnIndex: number | null;
  sortDirection: "asc" | "desc";
  onSortChange: (colIndex: number) => void;
}

export interface PrecisionRoundingConfig {
  countPrecision?: number;
  dollarPrecision?: number;
  numberPrecision?: number;
  ratioPrecision?: number;
  countRounding?: Report_RoundingValue;
  dollarRounding?: Report_RoundingValue;
  numberRounding?: Report_RoundingValue;
  ratioRounding?: Report_RoundingValue;
}

export function getPrecisionRoundingForValueType(
  valueType?: Report_ValueType,
  precisionRoundingConfig?: PrecisionRoundingConfig
) {
  const defaultConfig = {
    countPrecision: 0,
    dollarPrecision: 0,
    numberPrecision: 2,
    ratioPrecision: 2,
    countRounding: Report_RoundingValue.None,
    dollarRounding: Report_RoundingValue.None,
    numberRounding: Report_RoundingValue.None,
    ratioRounding: Report_RoundingValue.None,
  };
  switch (valueType) {
    case Report_ValueType.Count:
      return {
        precision: precisionRoundingConfig?.countPrecision ?? defaultConfig.countPrecision,
        rounding: precisionRoundingConfig?.countRounding ?? defaultConfig.countRounding,
      };
    case Report_ValueType.Dollar:
      return {
        precision: precisionRoundingConfig?.dollarPrecision ?? defaultConfig.dollarPrecision,
        rounding: precisionRoundingConfig?.dollarRounding ?? defaultConfig.dollarRounding,
      };
    case Report_ValueType.Ratio:
      return {
        precision: precisionRoundingConfig?.ratioPrecision ?? defaultConfig.ratioPrecision,
        rounding: precisionRoundingConfig?.ratioRounding ?? defaultConfig.ratioRounding,
      };
    case Report_ValueType.Number:
    default:
      return {
        precision: precisionRoundingConfig?.numberPrecision ?? defaultConfig.numberPrecision,
        rounding: precisionRoundingConfig?.numberRounding ?? defaultConfig.numberRounding,
      };
  }
}

export function getDisplayForCell(
  cell: PaxTableCell | undefined,
  precisionRoundingConfig?: PrecisionRoundingConfig
) {
  const valueType = cell?.valueType ?? Report_ValueType.String;
  const { precision, rounding } = getPrecisionRoundingForValueType(
    valueType,
    precisionRoundingConfig
  );
  const valueToUse =
    valueType === Report_ValueType.String || valueType === Report_ValueType.Date
      ? (cell?.displayValue ?? cell?.value)
      : cell?.value;
  return formatDisplayValue(valueToUse, valueType, precision, rounding);
}

export function compareKeys(
  a: Maybe<number> | undefined,
  b: Maybe<number> | undefined,
  sortDirection?: SortDirection
): number {
  const dir = sortDirection ?? SortDirection.ASCENDING;
  return a == undefined || b == undefined
    ? cmpNullKeys(a, b)
    : a < 0 || b < 0
      ? cmpNegKeys(a, b)
      : dir * (a - b);
}

export function cmpNullKeys(a: Maybe<number> | undefined, b: Maybe<number> | undefined): number {
  return b != undefined ? -1 : a != undefined ? 1 : 0;
}

export function cmpNegKeys(a: number, b: number): number {
  return b >= 0 ? 1 : a >= 0 ? -1 : a - b;
}

function getCellSortValue(cell: PaxTableCell): string | number {
  if (cell.sortKey != null) return cell.sortKey;
  if (cell.altSortKey != null) return cell.altSortKey;
  const stringTypes = [Report_ValueType.String, Report_ValueType.Date];
  if (cell.valueType == null || !stringTypes.includes(cell.valueType)) {
    const parsed = parseFloat(cell.value ?? "");
    if (!isNaN(parsed)) return parsed;
  }
  return cell.displayValue ?? cell.value ?? "";
}

export function sortRows(rows: PaxTableRow[], sortConfig: SortConfig): PaxTableRow[] {
  const sortKeyCol = sortConfig.sortColumnIndex;
  const sortKeyDir = sortConfig.sortDirection;
  const altKeyCol = sortConfig.sortColumnIndex;
  const altKeyDir = sortConfig.sortDirection;
  const sortedRows = [...rows];

  if (sortKeyCol != null && sortKeyDir) {
    sortedRows.sort((a, b) => {
      const valueType = a.cells[sortKeyCol].valueType;

      // prioritize using the returned value type
      const valA = getCellSortValue(a.cells[sortKeyCol]);
      const valB = getCellSortValue(b.cells[sortKeyCol]);

      // sorts by value type
      if (valueType) {
        let valueTypeComparison: number | string | Date;

        switch (valueType) {
          case Report_ValueType.Count:
            valueTypeComparison = (valA as number) - (valB as number);
            break;
          case Report_ValueType.Date:
            valueTypeComparison = new Date(valA).getTime() - new Date(valB).getTime();
            break;
          case Report_ValueType.Dollar:
            valueTypeComparison = (valA as number) - (valB as number);
            break;
          case Report_ValueType.Number:
            valueTypeComparison = (valA as number) - (valB as number);
            break;
          case Report_ValueType.Ratio:
            valueTypeComparison = (valA as number) - (valB as number);
            break;
          default: {
            // "String" is the generic/fallback value type, but that doesn't
            // mean the underlying data is actually textual — a column can be
            // typed String while still carrying a numeric `sortKey` (e.g. "#
            // of Loans"). Respect that numeric key when it's present, so we
            // don't fall back to comparing display strings ("2" vs "10" sorts
            // lexicographically, i.e. by digit count, not numeric value).
            const keyA = a.cells[sortKeyCol].sortKey;
            const keyB = b.cells[sortKeyCol].sortKey;
            valueTypeComparison =
              keyA != null && keyB != null
                ? compareKeys(keyA, keyB, 1) // direction applied once, below
                : String(valA).localeCompare(String(valB));
          }
        }
        return sortKeyDir === "asc" ? valueTypeComparison : -valueTypeComparison;
      }

      // if no value type provided, fall back to the sort key
      const keyA = a.cells[sortKeyCol].sortKey;
      const keyB = b.cells[sortKeyCol].sortKey;

      // if no sort keys provided, fall back to using raw values
      if (keyA == null || keyB == null) {
        let comparison = 0;
        if (typeof valA === "number" && typeof valB === "number") {
          comparison = valA - valB;
        } else {
          comparison = String(valA).localeCompare(String(valB));
        }
        return sortKeyDir === "asc" ? comparison : -comparison;
      }

      // using sort keys we grabbed earlier
      const res = compareKeys(keyA, keyB, sortKeyDir === "asc" ? 1 : -1);

      if (res === 0 && altKeyCol != undefined) {
        const altKeyA = a.cells[altKeyCol].altSortKey;
        const altKeyB = b.cells[altKeyCol].altSortKey;
        return compareKeys(altKeyA, altKeyB, altKeyDir === "asc" ? 1 : -1);
      }
      return res;
    });

    // This is a temporary workaround for making sure the Total row is always at the
    // bottom since the mock data does not have any sort keys set. When real data
    // is integrated this section can likely be removed.
    // =======================================

    const totalRowIdx = sortedRows.findIndex(({ cells }) => {
      const idx = cells.findIndex(({ displayValue }) => displayValue === "Total");
      return idx != -1;
    });

    if (totalRowIdx != -1) {
      const [totalRow] = sortedRows.splice(totalRowIdx, 1);
      sortedRows.push(totalRow);
    }

    // =======================================
  }
  return sortedRows;
}

const baseHeaderProps: TypographyProps = {
  component: "div",
  variant: "caption",
  fontWeight: 600,
};

//TODO: deprecate using this function directly in favor of createReportingElement()
export function resolveElementData(
  elementType: ElementType,
  elementData: ReportElementData,
  props: PaxElementWrapperProps & any,
  theme: Theme,
  sortConfig?: SortConfig,
  isLoading?: boolean,
  headerOverwrites?: Partial<TypographyProps>,
  navigate?: UseNavigateResult<string>
): JSX.Element {
  const headerProps = {
    ...baseHeaderProps,
    ...headerOverwrites,
  };

  switch (elementType) {
    case ElementType.List:
      return resolveListData(elementData, props, theme, headerProps, sortConfig, isLoading);
    case ElementType.Table:
      return resolveTableData(elementData, props, theme, headerProps, sortConfig);
    case ElementType.Matrix:
      return resolveMatrixData(elementData, props, theme, headerProps, navigate!);
    default:
      return <></>;
  }
}

export function renderHeaderDisplayName(
  name: string,
  { sx, ...headerProps }: TypographyProps,
  align?: "left" | "right" | "center"
) {
  const justifyContent =
    align === "right" ? "flex-end" : align === "center" ? "center" : "flex-start";
  return (
    <Box sx={{ minWidth: 0 }} justifyContent={justifyContent}>
      <Typography
        {...headerProps}
        sx={[
          {
            textOverflow: "ellipsis",
            overflow: "hidden",
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {name}
      </Typography>
    </Box>
  );
}

function renderTableSortHeader(
  header: PaxTableColumnHeader,
  idx: number,
  sortConfig: SortConfig,
  headerProps: TypographyProps,
  align: "left" | "right" | "center",
  theme: Theme,
  sortColIndex: number = idx,
  backgroundColor?: string
) {
  // SortableHeaderCell expects generic type but we use numeric indexes
  const SortableCell = SortableHeaderCell as any;
  return (
    <SortableCell
      key={idx}
      column={sortColIndex}
      colSpan={1}
      sortColumn={sortConfig.sortColumnIndex ?? -1}
      sortDirection={sortConfig.sortDirection}
      onSort={() => sortConfig.onSortChange(sortColIndex)}
      sx={{ backgroundColor }}
      data-testId={`header-${idx}`}
    >
      {header.displayName}
    </SortableCell>
  );
}
function resolveBoxProps(
  elementData: ReportElementData,
  props: PaxElementWrapperProps & Partial<PaxBoxElementProps>,
  precisionRoundingConfig?: PrecisionRoundingConfig
): PaxBoxElementProps & { title: string } {
  // get box-specific headers
  const headers = elementData.tableData?.headers ?? [];
  const totalIndex = headers.findIndex((h) => h.displayName === "Total");
  const totalHeader = totalIndex !== -1 ? headers[totalIndex].displayName : null;

  const percentIndex = headers.findIndex((h) => h.displayName === "% Difference");
  const percentChangeHeader = percentIndex !== -1 ? headers[percentIndex].displayName : null;

  const absoluteChangeIndex = headers.findIndex((h) => h.displayName === "Num. Difference");
  const absoluteChangeHeader =
    absoluteChangeIndex !== -1 ? headers[absoluteChangeIndex].displayName : null;

  const dataDateHeaders = headers.filter(
    (h) => ![totalHeader, percentChangeHeader, absoluteChangeHeader].includes(h.displayName)
  );

  // get row data and format it for the comparison columns
  const rows = elementData.tableData?.rows ?? [];
  const hydratedComparisonColumns: any[] = [];
  const showOptionName = props.title ?? rows[0].cells[0].displayValue;

  for (let i = 0; i < rows.length; i++) {
    const boxData: PaxTableCell[] = rows[i].cells;

    // if we queried for these values, add them
    let percentChangeValue = null;
    let absChangeValue = null;

    if (percentChangeHeader) {
      percentChangeValue = getDisplayForCell(boxData[percentIndex], precisionRoundingConfig);
    }
    if (absoluteChangeHeader) {
      absChangeValue = getDisplayForCell(boxData[absoluteChangeIndex], precisionRoundingConfig);
    }

    // last value in the row corresponds to second/final data date of the box element
    const comparisonValue = getDisplayForCell(boxData[boxData.length - 1], precisionRoundingConfig);

    const comparisonColumn = {
      headerLabel: dataDateHeaders[dataDateHeaders.length - 1].displayName,
      comparisonValue: comparisonValue ?? "-",
      row1Label: percentChangeHeader,
      row2Label: absoluteChangeHeader,
      chipLabel: percentChangeValue ?? "-",
      chipVariant: props?.columns?.[i].chipVariant ?? "soft",
      chipColor: props?.columns?.[i].chipColor ?? "default",
      diffValue: absChangeValue ?? "-",
    };
    hydratedComparisonColumns.push(comparisonColumn);
  }

  return {
    primaryValue: getDisplayForCell(rows?.[0]?.cells?.[1], precisionRoundingConfig),
    columns: hydratedComparisonColumns,
    showComparisonRow1: props?.showComparisonRow1 ?? true,
    showComparisonRow2: props?.showComparisonRow2 ?? true,
    boxElementWidth: props?.boxElementWidth ?? undefined,
    title: showOptionName ?? "",
  };
}

const ROUNDING_DIVISORS: Record<AxisRounding, number> = {
  none: 1,
  thousands: 1_000,
  millions: 1_000_000,
  billions: 1_000_000_000,
};

const ROUNDING_SUFFIXES: Record<AxisRounding, string> = {
  none: "",
  thousands: "K",
  millions: "M",
  billions: "B",
};

function formatAxisValue(value: number, fmt: AxisFormat, useTickPrecision = false): string {
  const divisor = ROUNDING_DIVISORS[fmt.rounding ?? "none"];
  const roundingSuffix = ROUNDING_SUFFIXES[fmt.rounding ?? "none"];
  const precision = useTickPrecision
    ? (fmt.tickPrecision ?? fmt.precision ?? 0)
    : (fmt.precision ?? 0);
  return (value / divisor).toFixed(precision) + roundingSuffix + (fmt.suffix ?? "");
}

function buildAxisFormatter(fmt: AxisFormat): () => string {
  return function (this: Highcharts.AxisLabelsFormatterContextObject) {
    return formatAxisValue(Number(this.value), fmt, true);
  };
}

function buildDataLabelFormatter(fmt: AxisFormat): () => string {
  return function (this: { y?: number | null }) {
    return this.y != null ? formatAxisValue(this.y, fmt) : "";
  };
}

function resolveColumnChartData(
  elementData: ReportElementData,
  axisFormats?: AxisFormat[],
  categoryLabels?: string[]
): Highcharts.Options {
  // all report service responses will come through as JSON
  if (elementData.chartData?.length) {
    return resolveChartData(elementData.chartData);
  }

  const { headers = [], rows = [] } = elementData.tableData ?? {};

  const series: Highcharts.SeriesColumnOptions[] = headers.map((header, colIndex) => {
    const fmt = axisFormats?.[colIndex];
    return {
      type: "column",
      name: header.displayName,
      yAxis: colIndex,
      data: rows.map((r) => {
        const cell = r.cells[colIndex + 1];
        const val = cell?.value != null ? parseFloat(cell.value) : null;
        return Number.isFinite(val) ? val : null;
      }),
      dataLabels: {
        enabled: true,
        formatter: fmt
          ? buildDataLabelFormatter(fmt)
          : function (this: { y?: number | null }) {
              return this.y != null ? String(this.y) : "";
            },
      },
    };
  });

  const yAxes: Highcharts.YAxisOptions[] = headers.map((header, colIndex) => {
    const fmt = axisFormats?.[colIndex];
    return {
      title: { text: fmt?.label ?? header.displayName },
      opposite: colIndex % 2 !== 0,
      labels: fmt ? { formatter: buildAxisFormatter(fmt) } : {},
      ...(fmt?.allowDecimals === false ? { allowDecimals: false, tickInterval: 1 } : {}),
    };
  });

  const categories =
    categoryLabels ?? rows.map((r) => r.cells[0]?.displayValue ?? r.cells[0]?.value ?? "");
  const xAxis: Highcharts.XAxisOptions = { categories };

  return {
    colors: [...nCinoColors],
    chart: { type: "column" },
    title: { text: undefined },
    xAxis,
    yAxis: yAxes,
    tooltip: { shared: true },
    legend: { enabled: true },
    credits: { enabled: false },
    plotOptions: { column: { dataLabels: { enabled: true } } },
    series,
  };
}

function resolveTrendChartData(
  elementData: ReportElementData,
  axisFormats?: AxisFormat[],
  categoryLabels?: string[]
): Highcharts.Options {
  // all report service responses will come through as JSON
  if (elementData.chartData?.length) {
    return resolveChartData(elementData.chartData);
  }

  const { headers = [], rows = [] } = elementData.tableData ?? {};
  const categories = [...headers].reverse().map((h) => h.displayName);
  // trend charts share a single y-axis; use the first axisFormat entry if provided
  const fmt = axisFormats?.[0];

  const series: Highcharts.SeriesSplineOptions[] = rows
    .filter((r) => r.cells.length > 0)
    .map((r, rowIndex) => ({
      type: "spline",
      name: categoryLabels?.[rowIndex] ?? `Series ${rowIndex + 1}`,
      data: [...r.cells]
        .reverse()
        .map((c) => (c.value && parseFloat(c.value) ? parseFloat(c.value) : null))
        .filter((v) => v != null),
      dataLabels: {
        enabled: true,
        formatter: fmt
          ? buildDataLabelFormatter(fmt)
          : function (this: { y?: number | null }) {
              return this.y != null ? String(this.y) : "";
            },
      },
    }));

  const xAxis: Highcharts.XAxisOptions = { categories, labels: { rotation: 0 } };

  if (series.length === 0) {
    return { series: [], xAxis };
  }

  return {
    colors: [...nCinoColors],
    chart: { type: "spline" },
    title: { text: undefined },
    xAxis,
    yAxis: [
      {
        title: { text: fmt?.label ?? undefined },
        labels: fmt ? { formatter: buildAxisFormatter(fmt) } : {},
      },
    ],
    tooltip: { shared: true },
    legend: { enabled: series.length > 1 },
    credits: { enabled: false },
    plotOptions: { spline: { dataLabels: { enabled: true }, marker: { enabled: true } } },
    series,
  };
}

function resolveTableData(
  elementData: ReportElementData,
  props: PaxTableProps & {
    tableHeaderVariant?: "dark" | "light";
    settings?: Report_ElementQueryOptionsInput;
  },
  theme: Theme,
  headerProps: TypographyProps,
  sortConfig?: SortConfig
): JSX.Element {
  const tableData = elementData.tableData ?? { headers: [], rows: [] };

  const headers: PaxTableColumnHeader[] = tableData.headers;
  const rows: PaxTableRow[] = tableData.rows ? tableData.rows : [];

  let formattedHeaders: JSX.Element = <></>;
  let formattedBody: JSX.Element = <></>;

  const displayHeaders = headers;
  const displayRows = sortConfig ? sortRows(rows, sortConfig) : rows;
  const headerRowBg =
    (props.tableHeaderVariant ?? "light") === "dark"
      ? `${theme?.palette?.background?.default} !important`
      : "#ffffff !important";
  const headerSX = { whiteSpace: "nowrap", backgroundColor: headerRowBg };

  formattedHeaders = (
    <TableHead>
      <TableRow>
        {displayHeaders.map((h: PaxTableColumnHeader, i) => {
          const align = h.align ?? (i > 0 ? "right" : "left");
          if (h.sortable && sortConfig) {
            return renderTableSortHeader(
              h,
              i,
              sortConfig,
              headerProps,
              align,
              theme,
              i,
              headerRowBg
            );
          }
          return (
            <TooltipCell
              key={i}
              sx={headerSX}
              align={align}
              title={h.displayName}
              testId={`header-${i}`}
            >
              {renderHeaderDisplayName(h.displayName, headerProps, align)}
            </TooltipCell>
          );
        })}
      </TableRow>
    </TableHead>
  );

  formattedBody = (
    <TableBody>
      {displayRows.map((r: PaxTableRow, i) => {
        let cellSX: SxProps = { whiteSpace: "nowrap" };

        // apply styling to rows based on row class
        switch (r.rowClass) {
          case RowClass.SUBHEADER:
            cellSX = {
              ...cellSX,
              backgroundColor: theme?.palette?.background?.default ?? "#FAFAFA",
              fontWeight: "bold",
            };
            break;
          case RowClass.SUBTOTAL:
            cellSX = { ...cellSX, fontWeight: "bold" };
            break;
          case RowClass.TOTAL:
            cellSX = {
              ...cellSX,
              backgroundColor: theme?.palette?.background?.default ?? "#FAFAFA",
              fontWeight: "bold",
            };
            break;
          case RowClass.ALTERNATE:
            if (props.striped) {
              cellSX = { ...cellSX, backgroundColor: props.stripeColor };
            }
            break;
          case RowClass.SUBTOTAL_ALTERNATE:
            cellSX = { ...cellSX, fontWeight: "bold" };
            if (props.striped) {
              cellSX = { ...cellSX, backgroundColor: props.stripeColor };
            }
            break;
        }
        return (
          <TableRow className={".PaxElementWrapper-root.PaxTableListBodyRow-root"} key={i}>
            {r.cells.map((c: PaxTableCell, j) => {
              const displayValue = getDisplayForCell(
                c,
                getPrecisionRoundingFromSettings(props.settings)
              );
              if (c.jsx) {
                return (
                  <TooltipCell
                    title={displayValue}
                    key={j}
                    sx={{ ...cellSX }}
                    testId={`row-${i}-cell-${j}`}
                  >
                    {c.jsx}
                  </TooltipCell>
                );
              } else {
                return (
                  <TooltipCell
                    title={displayValue}
                    key={j}
                    sx={{ ...cellSX, textAlign: j > 0 ? "right" : "left" }}
                    testId={`row-${i}-cell-${j}`}
                  >
                    {displayValue}
                  </TooltipCell>
                );
              }
            })}
          </TableRow>
        );
      })}
    </TableBody>
  );

  const tableChild = (
    <>
      {formattedHeaders}
      {formattedBody}
    </>
  );

  return tableChild;
}

export function resolveListData(
  elementData: ReportElementData,
  props: PaxListElementProps,
  theme: Theme,
  headerProps: TypographyProps,
  sortConfig?: SortConfig,
  isLoading?: boolean
): JSX.Element {
  const tableData = elementData.tableData ?? { headers: [], rows: [] };

  const headers: PaxTableColumnHeader[] = tableData.headers;
  const rows: PaxTableRow[] = tableData.rows ? tableData.rows : [];

  const showIndex = props.showIndex ?? true; // default to current behavior

  let formattedHeaders: JSX.Element = <></>;
  let formattedBody: JSX.Element = <></>;

  let displayHeaders = headers;
  // sort before prepending index column so sortColumnIndex aligns with original headers
  const sortedRows = sortConfig ? sortRows(rows, sortConfig) : rows;
  let displayRows = sortedRows;

  const precisionRoundingConfig = getPrecisionRoundingFromSettings(props.settings);

  // if a list, add the index of each row to a new column
  if (showIndex) {
    displayHeaders = [{ displayName: "", type: ColumnType.String }, ...headers];
    displayRows = sortedRows.map((row, index) => ({
      ...row,
      cells: [{ displayValue: String(index + 1), bold: true }, ...row.cells],
    }));
  }

  // enable row-level selection
  if (props.selectable) {
    const selectedRows = props.selectedRows ?? [];
    const selectedCount = selectedRows.filter(Boolean).length;
    const derivedAllSelected = selectedRows.length > 0 && selectedCount === rows.length;
    // Callers driving a whole-set (across pages) selection can override the page-scoped
    // derivation so the header checkbox reflects the true selection state.
    const allSelected = props.selectAllChecked ?? derivedAllSelected;
    const someSelected = props.selectAllIndeterminate ?? (selectedCount > 0 && !derivedAllSelected);

    displayHeaders = [
      {
        jsx: (
          <Box>
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={props.onSelectAll ?? (() => {})}
              aria-label="Select All"
              sx={{ padding: 0 }}
              data-testid={`paxList-checkbox-selectAll`}
            />
          </Box>
        ),
        displayName: "",
        type: ColumnType.JSX,
      },
      ...displayHeaders,
    ];
    displayRows = displayRows.map((row, index) => {
      return {
        ...row,
        cells: [
          {
            jsx: (
              <Checkbox
                checked={selectedRows[index] ?? false}
                onChange={() => props.onSelect?.(index)}
                sx={{ padding: 0 }}
                data-testid={`paxList-checkbox-${index}`}
              />
            ),
          },
          ...row.cells,
        ],
      };
    });
  }

  const totalRows = displayRows.filter((r) => r.rowClass === RowClass.TOTAL);
  const dataRowsOnly = displayRows.filter((r) => r.rowClass !== RowClass.TOTAL);

  // slice the data to paginate properly
  if (props.pagination) {
    displayRows = props.pagination
      ? dataRowsOnly.slice(
          props.pagination.page * props.pagination.rowsPerPage,
          (props.pagination.page + 1) * props.pagination.rowsPerPage
        )
      : [];
  } else {
    displayRows = dataRowsOnly;
  }

  // displayHeaders has optional prepended columns (index + optional checkbox) before real headers
  const prependedColCount = (props.selectable ? 1 : 0) + (showIndex ? 1 : 0);
  const headerRowBg =
    (props.tableHeaderVariant ?? "light") === "dark"
      ? `${theme?.palette?.background?.default} !important`
      : "#ffffff !important";
  const headerSX = {
    flexDirection: "row-reverse",
    whiteSpace: "nowrap",
    backgroundColor: headerRowBg,
  };

  formattedHeaders = (
    <TableHead>
      <TableRow>
        {displayHeaders.map((h: PaxTableColumnHeader, i) => {
          const originalColIndex = i - prependedColCount;
          const isFirstCol = originalColIndex <= 0;
          const align = h.align ?? (isFirstCol ? "left" : "right");
          if (h.jsx) {
            return (
              <TableCell key={i} padding="checkbox" sx={{ backgroundColor: headerRowBg }}>
                <PaxTooltip title={h.displayName} placement="top" testId={`header-${i}`}>
                  {h.jsx}
                </PaxTooltip>
              </TableCell>
            );
          } else if (h.sortable && sortConfig) {
            return renderTableSortHeader(
              h,
              i,
              sortConfig,
              headerProps,
              align,
              theme,
              originalColIndex,
              headerRowBg
            );
          }

          return (
            <TooltipCell
              key={i}
              align={align}
              sx={headerSX}
              title={h.displayName}
              testId={`header-${i}`}
            >
              {renderHeaderDisplayName(h.displayName, headerProps, align)}
            </TooltipCell>
          );
        })}
      </TableRow>
    </TableHead>
  );

  formattedBody = (
    <TableBody>
      {isLoading ? (
        <>
          {Array.from({ length: 3 }).map((_, i) => (
            <TableRow key={`skeleton_${i}`} sx={{ height: 73 }}>
              {displayHeaders.map((_, colIndex) => (
                <TableCell key={`skeleton_${i}_col_${colIndex}`} sx={{ padding: "16px" }}>
                  <Skeleton variant="rectangular" height={24} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </>
      ) : (
        displayRows.map((r: PaxTableRow, i) => {
          let cellSX: SxProps = { whiteSpace: "nowrap" };

          // apply styling to rows based on row class
          switch (r.rowClass) {
            case RowClass.SUBHEADER:
              cellSX = {
                ...cellSX,
                backgroundColor: theme?.palette?.background?.default ?? "#FAFAFA",
                fontWeight: "bold",
              };
              break;
            case RowClass.SUBTOTAL:
              cellSX = { ...cellSX, fontWeight: "bold" };
              break;
            case RowClass.TOTAL:
              cellSX = {
                ...cellSX,
                backgroundColor: theme?.palette?.background?.default ?? "#FAFAFA",
                fontWeight: "bold",
              };
              break;
            case RowClass.ALTERNATE:
              if (props.striped) {
                cellSX = { ...cellSX, backgroundColor: props.stripeColor };
              }
              break;
            case RowClass.SUBTOTAL_ALTERNATE:
              cellSX = { ...cellSX, fontWeight: "bold" };
              if (props.striped) {
                cellSX = { ...cellSX, backgroundColor: props.stripeColor };
              }
              break;
          }

          return (
            <TableRow className={".PaxElementWrapper-root.PaxTableListBodyRow-root"} key={i}>
              {r.cells.map((c: PaxTableCell, j) => {
                return (
                  <TooltipCell
                    key={j}
                    sx={{ ...cellSX, textAlign: j > 0 ? "right" : "left" }}
                    align={j > prependedColCount ? "right" : "left"}
                    title={getDisplayForCell(c, precisionRoundingConfig)}
                    testId={`row-${i}-cell-${j}`}
                  >
                    {c.jsx ? c.jsx : getDisplayForCell(c, precisionRoundingConfig)}
                  </TooltipCell>
                );
              })}
            </TableRow>
          );
        })
      )}
    </TableBody>
  );

  const formattedFooter: JSX.Element =
    totalRows.length > 0 && !props.isEmpty ? (
      <TableFooter>
        {totalRows.map((r: PaxTableRow, i) => {
          const cellSX = getRowClassSx(r.rowClass, theme, props.striped, props.stripeColor);
          return (
            <TableRow
              className={".PaxElementWrapper-root.PaxTableListBodyRow-root"}
              key={`total-${i}`}
            >
              {r.cells.map((c: PaxTableCell, j) => {
                return (
                  <TooltipCell
                    key={j}
                    sx={{ ...cellSX, textAlign: j > 0 ? "right" : "left" }}
                    align={j > prependedColCount ? "right" : "left"}
                    title={getDisplayForCell(c, precisionRoundingConfig)}
                    testId={`footer-${i}`}
                  >
                    {c.jsx ? c.jsx : getDisplayForCell(c, precisionRoundingConfig)}
                  </TooltipCell>
                );
              })}
            </TableRow>
          );
        })}
      </TableFooter>
    ) : (
      <></>
    );

  const tableChild = (
    <>
      {formattedHeaders}
      {formattedBody}
      {formattedFooter}
    </>
  );

  return tableChild;
}

export function getPrecisionRoundingFromSettings(
  settings: Report_ElementQueryOptionsInput | null | undefined
) {
  const precisionRoundingConfig: PrecisionRoundingConfig = {
    countRounding: settings?.countRounding as Report_RoundingValue,
    countPrecision: settings?.countPrecision as number,
    dollarRounding: settings?.dollarRounding as Report_RoundingValue,
    dollarPrecision: settings?.dollarPrecision as number,
    numberRounding: settings?.numberRounding as Report_RoundingValue,
    numberPrecision: settings?.numberPrecision as number,
    ratioRounding: settings?.ratioRounding as Report_RoundingValue,
    ratioPrecision: settings?.ratioPrecision as number,
  };
  return precisionRoundingConfig;
}

function getRowClassSx(
  rowClass: string | null | undefined,
  theme: Theme,
  striped?: boolean,
  stripeColor?: string
): SxProps {
  let cellSX: SxProps = { whiteSpace: "nowrap" };

  switch (rowClass) {
    case RowClass.SUBHEADER:
      cellSX = {
        ...cellSX,
        backgroundColor: theme?.palette?.background?.default ?? "#FAFAFA",
        fontWeight: "bold",
      };
      break;
    case RowClass.SUBTOTAL:
      cellSX = { ...cellSX, fontWeight: "bold" };
      break;
    case RowClass.TOTAL:
      cellSX = {
        ...cellSX,
        backgroundColor: theme?.palette?.background?.default ?? "#FAFAFA",
        fontWeight: "bold",
        fontSize: 14,
        color: theme?.palette?.text?.primary ?? "rgb(0,0,0,0.87)",
      };
      break;
    case RowClass.ALTERNATE:
      if (striped) {
        cellSX = { ...cellSX, backgroundColor: stripeColor };
      }
      break;
    case RowClass.SUBTOTAL_ALTERNATE:
      cellSX = { ...cellSX, fontWeight: "bold" };
      if (striped) {
        cellSX = { ...cellSX, backgroundColor: stripeColor };
      }
      break;
  }

  return cellSX;
}

export function createTableElement({ props, key }: ElementConfig) {
  return <PaxTableElement {...(props as PaxTableElementProps)} isElementTable key={key} />;
}

export function createListElement({ props, key }: ElementConfig) {
  return <PaxListElement {...(props as PaxListElementProps)} isElementTable key={key} />;
}

export function createBoxElement({ props, elementData, key }: ElementConfig) {
  // TODO: error / empty states
  const isError = elementData?.status === Report_ElementStatus.Failed;
  return (
    <PaxBoxElement
      {...props}
      {...(isError
        ? { primaryValue: "Error", title: props?.title ?? "" }
        : resolveBoxProps(elementData!, props as PaxBoxElementProps))}
      key={key}
    />
  );
}

export function createChartElement({ props, elementData, key }: ElementConfig) {
  const { title, elevated, sx } = props as PaxElementWrapperProps &
    ChartElementProps & { title: string };
  const hasData = (elementData?.chartData?.length ?? 0) > 0;
  if (!hasData) {
    return (
      <PaxElementWrapper {...props} key={key}>
        <CardContent>
          <PaxCallToAction icon={<EmptyBoxIllustration />} header="No results found" />
        </CardContent>
      </PaxElementWrapper>
    );
  }
  return (
    <PaxChartElement
      title={title ?? ""}
      elevated={elevated}
      sx={sx}
      options={resolveChartData(elementData!.chartData)}
      key={key}
    />
  );
}

export function createColumnChartElement({ props, elementData, key }: ElementConfig) {
  const { title, elevated, sx, axisFormats, categoryLabels } = props as PaxElementWrapperProps &
    ChartElementProps & { title: string };
  return (
    <PaxChartElement
      title={title ?? ""}
      elevated={elevated}
      sx={sx}
      options={resolveColumnChartData(elementData!, axisFormats, categoryLabels)}
      key={key}
    />
  );
}

export function createTrendChartElement({ props, elementData, key }: ElementConfig) {
  const { title, elevated, sx, axisFormats, categoryLabels } = props as PaxElementWrapperProps &
    ChartElementProps & { title: string };
  return (
    <PaxChartElement
      title={title ?? ""}
      elevated={elevated}
      sx={sx}
      options={resolveTrendChartData(elementData!, axisFormats, categoryLabels)}
      key={key}
    />
  );
}

function resolveBarChartData(
  elementData: ReportElementData,
  axisFormats?: AxisFormat[],
  categoryLabels?: string[],
  chartHeight?: number,
  showLegend?: boolean,
  pointWidth?: number
): Highcharts.Options {
  if (elementData.chartData?.length) {
    return resolveChartData(elementData.chartData);
  }

  const { headers = [], rows = [] } = elementData.tableData ?? {};

  const series: Highcharts.SeriesBarOptions[] = headers.map((header, colIndex) => {
    const fmt = axisFormats?.[colIndex];
    return {
      type: "bar",
      name: header.displayName,
      yAxis: colIndex,
      data: rows.map((r) =>
        r.cells[colIndex] != null ? parseFloat(r.cells[colIndex].value ?? "0") : null
      ),
      dataLabels: {
        formatter: fmt
          ? buildDataLabelFormatter(fmt)
          : function (this: { y?: number | null }) {
              return this.y != null ? String(this.y) : "";
            },
      },
    };
  });

  const yAxes: Highcharts.YAxisOptions[] = headers.map((header, colIndex) => {
    const fmt = axisFormats?.[colIndex];
    return {
      title: { text: fmt?.label ?? header.displayName },
      opposite: colIndex % 2 !== 0,
      labels: fmt ? { formatter: buildAxisFormatter(fmt) } : {},
    };
  });

  const categories = categoryLabels ?? rows.map((_, i) => String(i + 1));
  const xAxis: Highcharts.XAxisOptions = { categories };

  return {
    colors: [...nCinoColors],
    chart: { type: "bar", ...(chartHeight != null ? { height: chartHeight } : {}) },
    title: { text: undefined },
    xAxis,
    yAxis: yAxes,
    tooltip: { shared: true },
    legend: { enabled: showLegend ?? true },
    credits: { enabled: false },
    plotOptions: {
      bar: {
        colorByPoint: true,
        dataLabels: { enabled: true },
        pointWidth: pointWidth ? pointWidth : undefined,
      },
    },
    series,
  };
}

export function createBarChartElement({ props, elementData }: ElementConfig) {
  const { title, elevated, sx, axisFormats, categoryLabels, chartHeight, showLegend, pointWidth } =
    props as PaxElementWrapperProps & ChartElementProps & { title: string };
  return (
    <PaxChartElement
      title={title ?? ""}
      elevated={elevated}
      sx={sx}
      options={resolveBarChartData(
        elementData!,
        axisFormats,
        categoryLabels,
        chartHeight,
        showLegend,
        pointWidth
      )}
    />
  );
}

export function createMatrixElement({ props, key }: ElementConfig) {
  return <PaxMatrixElement {...(props as PaxTableElementProps)} key={key} />;
}

export function createReportingElement(elementType: ElementType, config: ElementConfig) {
  const elementData =
    config.elementData ??
    (config.props as unknown as { elementData: ReportElementData | undefined }).elementData;

  const isError = elementData && elementData?.status === Report_ElementStatus.Failed;
  const isProcessing = elementData && elementData?.status !== Report_ElementStatus.Completed;

  // For List and Table elements, render the actual component with skeleton rows
  // instead of showing a generic loading state
  const showSkeletonInTable = false;

  if (isProcessing && !isError && !showSkeletonInTable) {
    return getLoaderForReportingElement(elementType, config);
  }
  if (isError) {
    return getErrorForReportingElement(config);
  }

  switch (elementType) {
    case ElementType.Table:
      return createTableElement(config);
    case ElementType.List:
      return createListElement(config);
    case ElementType.Box:
      return createBoxElement(config);
    case ElementType.Chart:
    case ElementType.TrendChart:
      return createChartElement(config);
    case ElementType.Matrix:
      return createMatrixElement(config);
  }
}

export function getLoaderForReportingElement(elementType: ElementType, config: ElementConfig) {
  const { title, sx, elevated } = config.props!;

  const _wrap = (node: JSX.Element) => (
    <PaxElementWrapper title={title ? title : "Loading..."} sx={sx} elevated={elevated}>
      <Box px="16px" py="38px">
        {node}
      </Box>
    </PaxElementWrapper>
  );

  switch (elementType) {
    case ElementType.Box:
      return _wrap(<PaxSkeletonLoader size="small" numRows={4} />);
    case ElementType.Table:
    case ElementType.List:
      return _wrap(<PaxSkeletonLoader size="small" numRows={8} />);
    case ElementType.Chart:
    case ElementType.TrendChart:
    case ElementType.Matrix:
    default:
      return _wrap(<PaxSkeletonLoader size="large" numRows={4} />);
  }
}

export function getErrorForReportingElement(config: ElementConfig, onErrorAction?: () => void) {
  const { title, sx, elevated } = config.props!;
  return (
    <PaxElementWrapper title={title ? title : "Error..."} sx={sx} elevated={elevated}>
      <Box px="16px" py="38px">
        <PaxCallToAction
          icon={<NetworkErrorIllustration />}
          header="Data failed to load."
          subtext="Please contact your customer support manager."
          onAction={onErrorAction ? onErrorAction : () => {}}
        />
      </Box>
    </PaxElementWrapper>
  );
}

// extra validation for hrefs generated from the report service here,
// but most of the heavy lifting is done by validation in the report service
// and use of the tanstack router useNavigate hook
export function toInternalPath(href: string): string | null {
  let url: URL;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) return null;
  return url.pathname + url.search + url.hash;
}
